/**
 * AnimatedButton — TouchableOpacity with scale animation and haptic feedback.
 */

import { useRef, useCallback } from 'react';
import { Animated, TouchableOpacity, TouchableOpacityProps, ViewStyle } from 'react-native';
import * as Haptics from 'expo-haptics';

interface AnimatedButtonProps extends TouchableOpacityProps {
  scaleDown?: number;
  haptic?: boolean;
  children: React.ReactNode;
}

export default function AnimatedButton({
  scaleDown = 0.95,
  haptic = true,
  children,
  style,
  onPressIn,
  onPressOut,
  onPress,
  ...rest
}: AnimatedButtonProps) {
  const scale = useRef(new Animated.Value(1)).current;

  const handlePressIn = useCallback(
    (e: any) => {
      Animated.spring(scale, {
        toValue: scaleDown,
        useNativeDriver: true,
        speed: 50,
        bounciness: 4,
      }).start();
      if (haptic) {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
      }
      onPressIn?.(e);
    },
    [scaleDown, haptic, onPressIn]
  );

  const handlePressOut = useCallback(
    (e: any) => {
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: true,
        speed: 50,
        bounciness: 8,
      }).start();
      onPressOut?.(e);
    },
    [onPressOut]
  );

  return (
    <TouchableOpacity
      activeOpacity={1}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onPress={onPress}
      {...rest}
      style={style}
    >
      <Animated.View style={{ transform: [{ scale }] }}>
        {children}
      </Animated.View>
    </TouchableOpacity>
  );
}
